import {
  VehicleCatalogMarket,
  VehicleCatalogMarketLabels,
  VehicleCatalogNodeKind,
  VehicleCatalogNodeKindLabels,
  type CarMatchingDetailsRecord
} from '@navio/nsdk';
import { NavioInfoCard, NoddiDialog } from 'noddi-ui-web';
import { formatCatalogYears, getLabelMapFromEntries } from 'noddi-util';

import { CatalogNodeFacts } from './CatalogNodeFacts';
import { matchedCatalogNode } from './matchedCatalogNode';

const KIND_LABEL = getLabelMapFromEntries(VehicleCatalogNodeKind, VehicleCatalogNodeKindLabels);
const MARKET_LABEL = getLabelMapFromEntries(VehicleCatalogMarket, VehicleCatalogMarketLabels);

type Props = {
  details: CarMatchingDetailsRecord | null | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

/**
 * Read-only popup over the catalog node this car is linked to: the factory trim that decides
 * tire sizes, wheel packages, bolt pattern and service values. Apps without the catalog pages
 * reach the same facts here; nothing on it edits.
 */
export const CarModificationDialog = ({ details, open, onOpenChange }: Props) => {
  const node = matchedCatalogNode(details);
  const isGeneration = node?.kind === VehicleCatalogNodeKind.GENERATION;
  const applicable = (details?.catalogNodes ?? []).filter(
    (entry) => entry.kind === VehicleCatalogNodeKind.MODIFICATION
  );

  return (
    <NoddiDialog
      open={open}
      onClose={() => onOpenChange(false)}
      title={node ? node.name : 'Modification'}
      description={
        node == null
          ? 'This car is not linked to the vehicle catalog.'
          : isGeneration
            ? 'Generation match: the catalog data below applies to the whole generation, the engine variant is unconfirmed.'
            : 'Catalog data for the factory trim this car is matched to.'
      }
      maxWidth='md'
    >
      {node == null ? (
        <p className='text-sm text-muted-foreground'>
          Without a catalog link the car has no factory wheel packages, bolt pattern, tire pressures or tightening
          torque. Refresh the car from the registry, then set the modification manually if the match stays open.
        </p>
      ) : (
        <div className='flex flex-col gap-4'>
          <NavioInfoCard
            title='Catalog identity'
            icon='Tag'
            columns={2}
            items={[
              { label: 'Catalog layer', value: KIND_LABEL[node.kind], readOnly: true, copyText: false },
              { label: 'Generation', value: node.parentName, readOnly: true },
              { label: 'Model years', value: formatCatalogYears(node.yearFrom, node.yearTo), readOnly: true },
              node.markets.length > 0 && {
                label: 'Sales regions',
                value: node.markets.map((market) => MARKET_LABEL[market]).join(', '),
                readOnly: true,
                copyText: false
              },
              { label: 'Provider slug', value: node.sourceSlug, readOnly: true },
              isGeneration &&
                applicable.length > 0 && {
                  label: 'Applicable modifications',
                  value: applicable.map((entry) => entry.name).join(', '),
                  readOnly: true
                }
            ]}
          />
          <CatalogNodeFacts node={node} showBodyPhotos={false} />
        </div>
      )}
    </NoddiDialog>
  );
};
